import { Actions, createStore } from '@bpd-library/utilities';
import {
    VirtualTourImageView,
    VirtualTourMinimap,
    VirtualView,
    VRTourPointerFilter,
    VRTourViewFilter,
} from '@features/virtual-tour/types/view';

interface VirtualTourStateItem {
    viewStack: string[];
    views: VirtualView[];
    currentView?: VirtualView;
    minimap?: VirtualTourMinimap;
}

export interface VirtualTourState {
    [id: string]: VirtualTourStateItem;
}

type VirtualTourStoreAction =
    | 'addVirtualTourData'
    | 'updateVirtualTourData'
    | 'pushView'
    | 'popView';

const initialState: VirtualTourState = {};

const getLastView = (tour: VirtualTourStateItem) => {
    const viewId = tour.viewStack[tour.viewStack.length - 1];
    const view = tour.views.find((v) => v.id === viewId);

    return view;
};

const actions: Actions<VirtualTourStoreAction, VirtualTourState> = {
    addVirtualTourData: (
        state: VirtualTourState,
        data: {
            tourId: string;
            views: VirtualView[];
            viewId?: string;
            minimap?: VirtualTourMinimap;
        },
    ) => {
        const { tourId, views, viewId, minimap } = data;
        const tour: VirtualTourStateItem = { views, viewStack: [viewId || views[0].id], minimap };
        tour.currentView = getLastView(tour);

        state[tourId] = tour;
        return state;
    },
    updateVirtualTourData: (
        state: VirtualTourState,
        data: {
            tourId: string;
            views: VirtualView[];
            viewId?: string;
            minimap?: VirtualTourMinimap;
        },
    ) => {
        const { tourId, views: newViews, viewId, minimap } = data;
        const filteredViews = newViews.filter(
            ({ id }) => !state[tourId].views.find((v) => v.id === id),
        );
        const tour: VirtualTourStateItem = {
            views: [...state[tourId].views, ...filteredViews],
            viewStack:
                viewId && viewId !== state[tourId].currentView?.id
                    ? [...state[tourId].viewStack, viewId]
                    : state[tourId].viewStack,
            minimap,
        };
        tour.currentView = getLastView(tour);

        state[tourId] = tour;
        return state;
    },
    pushView: (state: VirtualTourState, { tourId, viewId }: { tourId: string; viewId: string }) => {
        const tour = { ...state[tourId] };
        tour.viewStack = [...tour.viewStack, viewId];
        tour.currentView = getLastView(tour);

        state[tourId] = tour;
        return state;
    },
    popView: (state: VirtualTourState, tourId: string) => {
        const tour = { ...state[tourId] };
        tour.viewStack = tour.viewStack.slice(0, -1);
        tour.currentView = getLastView(tour);

        state[tourId] = tour;
        return state;
    },
};

export const pushView = (tourId: string, viewId: string) => {
    virtualTourStore.dispatch('pushView', { tourId, viewId });
};

export const popView = (tourId: string) => {
    virtualTourStore.dispatch('popView', tourId);
};

export const setData = (
    tourId: string,
    data: { views: VirtualView[]; firstViewId?: string | null; minimap?: VirtualTourMinimap },
) => {
    const { views, firstViewId, minimap } = data;
    const action: VirtualTourStoreAction = virtualTourStore.getState()[tourId]
        ? 'updateVirtualTourData'
        : 'addVirtualTourData';
    virtualTourStore.dispatch(action, {
        tourId,
        views,
        viewId: firstViewId,
        minimap,
    });
};

export const getViewInStore = (tourId: string, viewId: string) => {
    const vrState = virtualTourStore.getState();
    return vrState[tourId] && vrState[tourId].views.find((v) => v.id === viewId);
};

export const findPointerInCurrentView = (tourId: string, filter: VRTourPointerFilter) => {
    const currentView = virtualTourStore.getState()[tourId].currentView as VirtualTourImageView;
    if (currentView?.type !== '2d') return;
    return currentView.pointers.find(filter);
};

export const findView = (tourId: string, filter: VRTourViewFilter) => {
    const views = virtualTourStore.getState()[tourId].views;
    return views.find(filter);
};

export const virtualTourStore = createStore<VirtualTourState, VirtualTourStoreAction>({
    initialState,
    actions,
});
